import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { SharedHeader } from "@/components/SharedHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { 
  Building2, MapPin, Mail, Phone, CheckCircle, 
  Wifi, Car, Coffee, Users, Clock, DollarSign,
  ArrowLeft, Send
} from "lucide-react";

const VenuePartnershipPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    address: "",
    city: "",
    state: "MA",
    zip_code: "",
    description: "",
    venue_type: "",
    capacity: "",
    contact_name: user?.full_name || "",
    contact_email: user?.email || "",
    contact_phone: "",
    amenities: [],
    operating_hours: "",
    commission_rate: "20",
    additional_info: "",
  });

  const venueTypes = [
    "Restaurant/Cafe",
    "Game Store",
    "Community Center",
    "Bar/Lounge",
    "Private Club",
    "Event Space",
    "Other"
  ];

  const amenityOptions = [
    { id: "wifi", label: "Free WiFi", icon: Wifi },
    { id: "parking", label: "Parking Available", icon: Car },
    { id: "food", label: "Food Service", icon: Coffee },
    { id: "private_room", label: "Private Room", icon: Building2 },
    { id: "large_tables", label: "Large Tables", icon: Users },
    { id: "late_hours", label: "Late Hours", icon: Clock },
  ];

  const toggleAmenity = (amenityId) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenityId)
        ? prev.amenities.filter(a => a !== amenityId)
        : [...prev.amenities, amenityId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.address || !formData.city || !formData.venue_type || !formData.contact_email) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/venues/apply`, {
        name: formData.name,
        address: `${formData.address}, ${formData.city}, ${formData.state} ${formData.zip_code}`,
        city: formData.city,
        description: formData.description,
        venue_type: formData.venue_type,
        amenities: formData.amenities,
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone,
        commission_rate: parseFloat(formData.commission_rate) || 20,
        capacity: parseInt(formData.capacity) || 0,
        operating_hours: formData.operating_hours,
        contact_name: formData.contact_name,
        additional_info: formData.additional_info,
      });
      
      setSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit application");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050505]" data-testid="venue-partnership-page">
        <SharedHeader 
          activeSection={null}
          setActiveSection={() => {}}
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
        />

        <main className="pt-24 pb-16 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h1 className="font-bebas text-4xl tracking-wider text-white mb-4">
              APPLICATION SUBMITTED!
            </h1>
            <p className="text-[#A1A1AA] font-ubuntu mb-8">
              Thank you for your interest in becoming a Board2Boston™ venue partner. 
              Our team will review your application and contact you within 3-5 business days.
            </p>
            <div className="flex justify-center gap-4">
              <Button
                onClick={() => navigate("/home")}
                className="btn-gold"
              >
                Return Home
              </Button>
              <Button
                onClick={() => setSubmitted(false)}
                variant="outline"
                className="border-white/20 text-[#A1A1AA]"
              >
                Submit Another
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="venue-partnership-page">
      <SharedHeader 
        activeSection={null}
        setActiveSection={() => {}}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      <main className="pt-24 pb-16 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate("/home")}
            className="flex items-center gap-2 text-[#A1A1AA] hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-ubuntu text-sm">Back to Home</span>
          </button>

          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-[#7a6200]/20 flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-[#7a6200]" />
            </div>
            <h1 className="font-bebas text-4xl tracking-wider text-white mb-2">
              BECOME A VENUE PARTNER
            </h1>
            <p className="text-[#A1A1AA] font-ubuntu max-w-xl mx-auto">
              Partner with Board2Boston™ and host gaming events at your establishment. 
              Earn 15-25% commission on all events hosted at your venue.
            </p>
          </div>

          {/* Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="p-4 text-center">
                <DollarSign className="w-8 h-8 text-[#7a6200] mx-auto mb-2" />
                <h3 className="font-bebas text-lg text-white">EARN COMMISSION</h3>
                <p className="text-[#52525B] text-xs font-ubuntu">15-25% on entry fees</p>
              </CardContent>
            </Card>
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="p-4 text-center">
                <Users className="w-8 h-8 text-[#7a6200] mx-auto mb-2" />
                <h3 className="font-bebas text-lg text-white">MORE CUSTOMERS</h3>
                <p className="text-[#52525B] text-xs font-ubuntu">Attract gaming enthusiasts</p>
              </CardContent>
            </Card>
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="p-4 text-center">
                <Building2 className="w-8 h-8 text-[#7a6200] mx-auto mb-2" />
                <h3 className="font-bebas text-lg text-white">BUILD COMMUNITY</h3>
                <p className="text-[#52525B] text-xs font-ubuntu">Become a gaming hub</p>
              </CardContent>
            </Card>
          </div>

          {/* Application Form */}
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardHeader>
              <CardTitle className="font-bebas text-xl tracking-wider text-white">
                VENUE INFORMATION
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Venue Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                      Venue Name *
                    </label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="input-dark"
                      placeholder="Your venue name"
                      required
                      data-testid="venue-name-input"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                      Street Address *
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
                      <Input
                        value={formData.address}
                        onChange={(e) => setFormData({...formData, address: e.target.value})}
                        className="input-dark pl-10"
                        placeholder="123 Main Street"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">City *</label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      className="input-dark"
                      placeholder="Houston"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">State</label>
                      <Input
                        value={formData.state}
                        onChange={(e) => setFormData({...formData, state: e.target.value})}
                        className="input-dark"
                        placeholder="MA"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">ZIP</label>
                      <Input
                        value={formData.zip_code}
                        onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                        className="input-dark"
                        placeholder="02101"
                      />
                    </div>
                  </div>
                </div>

                {/* Venue Type */}
                <div>
                  <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-3">Venue Type *</label>
                  <div className="flex flex-wrap gap-2">
                    {venueTypes.map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setFormData({...formData, venue_type: type})}
                        className={`px-4 py-2 rounded text-sm font-ubuntu transition-colors ${
                          formData.venue_type === type
                            ? "bg-[#7a6200] text-black"
                            : "bg-white/5 text-[#A1A1AA] border border-white/10 hover:border-[#7a6200]"
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                    Venue Description
                  </label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="input-dark min-h-[100px]"
                    placeholder="Describe your venue, atmosphere, and what makes it great for gaming events..."
                  />
                </div>

                {/* Capacity & Hours */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                      Seating Capacity
                    </label>
                    <Input
                      type="number"
                      value={formData.capacity}
                      onChange={(e) => setFormData({...formData, capacity: e.target.value})}
                      className="input-dark"
                      placeholder="50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                      Operating Hours
                    </label>
                    <Input
                      value={formData.operating_hours}
                      onChange={(e) => setFormData({...formData, operating_hours: e.target.value})}
                      className="input-dark"
                      placeholder="Mon-Sun 11am-11pm"
                    />
                  </div>
                </div>

                {/* Amenities */}
                <div>
                  <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-3">Amenities</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {amenityOptions.map(({ id, label, icon: Icon }) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => toggleAmenity(id)}
                        className={`flex items-center gap-2 px-3 py-2 rounded text-sm font-ubuntu transition-colors ${
                          formData.amenities.includes(id)
                            ? "bg-[#7a6200]/20 text-[#7a6200] border border-[#7a6200]/50"
                            : "bg-white/5 text-[#A1A1AA] border border-white/10 hover:border-[#7a6200]/30"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Contact Info */}
                <div className="border-t border-white/10 pt-6">
                  <h3 className="font-bebas text-lg tracking-wider text-white mb-4">
                    CONTACT INFORMATION
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                        Contact Name *
                      </label>
                      <Input
                        value={formData.contact_name}
                        onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                        className="input-dark"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                        Contact Email *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
                        <Input
                          type="email"
                          value={formData.contact_email}
                          onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                          className="input-dark pl-10"
                          required
                          data-testid="venue-email-input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                        Contact Phone
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
                        <Input
                          value={formData.contact_phone}
                          onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                          className="input-dark pl-10"
                          placeholder="(555) 123-4567"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                        Preferred Commission Rate
                      </label>
                      <select
                        value={formData.commission_rate}
                        onChange={(e) => setFormData({...formData, commission_rate: e.target.value})}
                        className="input-dark w-full h-10"
                      >
                        <option value="15">15%</option>
                        <option value="20">20% (Standard)</option>
                        <option value="25">25%</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Additional Info */}
                <div>
                  <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                    Additional Information
                  </label>
                  <Textarea
                    value={formData.additional_info}
                    onChange={(e) => setFormData({...formData, additional_info: e.target.value})}
                    className="input-dark min-h-[80px]"
                    placeholder="Any other details you'd like us to know..."
                  />
                </div>

                {/* Submit */}
                <div className="flex justify-end pt-4 border-t border-white/10">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="btn-gold"
                    data-testid="submit-venue-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {loading ? "Submitting..." : "Submit Application"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>

      <footer className="border-t border-white/10 bg-[#0A0A0A] py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-[#52525B] font-ubuntu text-sm">
            © 2026 All Rights Reserved | Board2Boston™ | 713 Consulting and Development
          </p>
          <p className="text-[#52525B] font-ubuntu text-xs mt-2">
            1942 W. Gray St., Box #1259 | Houston, TX 77019 | United States
          </p>
        </div>
      </footer>
    </div>
  );
};

export default VenuePartnershipPage;
